<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <!-- eslint-disable prefer-standard-components/no-vuetify-inputs -->
  <div>
    <div v-if="properties.role == 'formula' && properties.type != 'array'">
      <v-textarea
        v-if="objectArraysAsDataTable"
        v-model="localValue"
        @change="$emit('change', $event)"
        :append-inner-icon="processing ? 'pending' : 'lock'"
        :aria-label="`${$bct('Enter')} ${label}`"
        :autofocus="autofocus"
        :disabled="locked || undefined"
        :required="mandatory || undefined"
        :rules="rules"
        data-cy="field-input"
        density="compact"
        prefix="="
        rows="1"
        variant="filled"
        auto-grow
        hide-details
        no-resize
      />

      <v-text-field
        v-else
        v-model="localValue"
        @change="$emit('change', $event)"
        :append-inner-icon="processing ? 'pending' : 'lock'"
        :aria-label="`${$bct('Enter')} ${label}`"
        :autofocus="autofocus"
        :density="density"
        :required="mandatory || undefined"
        :rules="rules"
        data-cy="field-input"
        prefix="="
        variant="filled"
        disabled
        hide-details
      />
    </div>

    <template v-else>
      <div
        v-if="properties.type == 'array'"
        class="d-flex flex-column ga-4"
      >
        <template v-if="properties.items.type == 'object'">
          <v-btn-toggle
            v-if="objectArraysAsDataTable && locked && $vuetify.display.smAndDown"
            v-model="objectArraysModelValue"
            class="d-flex justify-end"
            variant="outlined"
            divided
            mandatory
          >
            <v-btn icon="view_agenda" />
            <v-btn icon="table_chart" />
          </v-btn-toggle>

          <template v-if="objectArraysModelValue === 1">
            <DataTable
              :headers="tableHeaders"
              :items="localValue"
              :items-per-page="localValue.length"
              :no-data-text="'No entries'"
              class="b-radius-0 b-1 bc-outlined-gray pb-4-table"
              data-testid="field-data-table"
              density="comfortable"
              item-key="id"
              hide-default-footer
            >
              <template #item="{ item, index }">
                <tr
                  v-if="properties.enabled"
                  class="text-no-wrap"
                >
                  <td
                    v-for="(field, fieldIndex) in itemFields"
                    :key="[itemIndex, field].join('.')"
                    :class="{
                      'ps-3': fieldIndex === 0,
                    }"
                    class="py-3 pe-3 ps-0 va-top"
                  >
                    <Field
                      v-model="item[field[0]]"
                      @change="$emit('change', $event)"
                      @input="inputEventHandler()"
                      :append-inner-icon="appendInnerIcon"
                      :booleans-as-checkboxes="booleansAsCheckboxes"
                      :hard-lock="locked || undefined"
                      :multiple-boolean-fields="multipleBooleanFields"
                      :object-arrays-as-data-table="locked"
                      :properties="field[1]"
                      :readonly="readonly || undefined"
                      :required="mandatory || undefined"
                      :rules="rules"
                      :sibling-values="modelValue?.[0] || {}"
                      condensed
                    />
                  </td>

                  <td v-if="!locked">
                    <v-btn
                      @click="removeItemByIndex(index)"
                      class="focus-very-visible"
                      color="red"
                      variant="text"
                    >
                      {{ $bct('Remove') }}
                    </v-btn>
                  </td>
                </tr>
              </template>
            </DataTable>
          </template>

          <template v-else>
            <v-card
              v-for="(item, itemIndex) in localValue"
              :key="itemIndex"
              data-testid="field-item-card"
              border
              tile
            >
              <v-card-text>
                <v-row dense>
                  <LabeledControl
                    v-for="field in itemFields"
                    :key="[itemIndex, field].join('.')"
                    :cols="field[1].format == 'date' ? '12' : groupedFieldCols"
                    :format="field[1].format"
                    :message="field[1].alias || field[1].title"
                    :type="field[1].type"
                  >
                    <Field
                      v-model="item[field[0]]"
                      @change="$emit('change', $event)"
                      @input="inputEventHandler()"
                      :append-inner-icon="appendInnerIcon"
                      :booleans-as-checkboxes="booleansAsCheckboxes"
                      :hard-lock="locked || undefined"
                      :multiple-boolean-fields="multipleBooleanFields"
                      :properties="field[1]"
                      :readonly="readonly || undefined"
                      :required="mandatory || undefined"
                      :rules="rules"
                      :sibling-values="item || {}"
                      condensed
                    />
                  </LabeledControl>
                </v-row>
              </v-card-text>

              <v-card-actions v-if="!locked">
                <v-btn
                  @click="removeItemByIndex(itemIndex)"
                  class="focus-very-visible"
                  color="red"
                  variant="text"
                >
                  {{ $bct('Remove') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </template>

          <v-card-actions v-if="!locked">
            <v-btn
              @click="addItem(properties.items.properties)"
              class="focus-very-visible"
              color="primary"
            >
              {{ $bct('Add') }}
            </v-btn>
          </v-card-actions>
        </template>

        <template v-else>
          <v-row dense>
            <v-col>
              <template v-if="properties.items.enum?.length > 10 || condensed">
                <v-select
                  v-model="localValue"
                  @update:model-value="$emit('change', $event)"
                  :append-inner-icon="appendInnerIcon"
                  :aria-label="$bct(`Select ${label}`)"
                  :density="density"
                  :disabled="locked || undefined"
                  :items="enumItems"
                  :readonly="readonly || undefined"
                  :required="mandatory || undefined"
                  :rules="rules"
                  data-cy="field-input"
                  variant="filled"
                  chips
                  clearable
                  closable-chips
                  hide-details
                  multiple
                />
              </template>

              <template v-else>
                <v-checkbox
                  v-for="(option, index) in properties.items.enum"
                  v-model="localValue"
                  @update:model-value="$emit('change', $event)"
                  :key="index"
                  :append-inner-icon="appendInnerIcon"
                  :aria-label="$bct(option)"
                  :autofocus="autofocus"
                  :disabled="locked || undefined"
                  :label="$bct(option)"
                  :readonly="readonly || undefined"
                  :required="mandatory || undefined"
                  :rules="rules"
                  :value="option"
                  class="bg-super-light-blue pa-2 bc-extra-light-gray b-1 mx-0 my-1"
                  data-cy="field-input"
                  hide-details
                  multiple
                />
              </template>
            </v-col>
          </v-row>
        </template>
      </div>

      <div v-if="properties.type == 'boolean'">
        <v-row dense>
          <template v-if="multipleBooleanFields && booleansAsCheckboxes">
            <v-col cols="12">
              <v-checkbox
                v-model="localValue"
                @update:model-value="$emit('change', $event)"
                :append-inner-icon="appendInnerIcon"
                :density="density"
                :disabled="locked || undefined"
                :label="checkboxLabel"
                :readonly="readonly || undefined"
                :required="mandatory || undefined"
                :rules="rules"
                class="bg-super-light-blue pa-2 bc-extra-light-gray b-1"
                data-cy="field-input"
                hide-details
              />
            </v-col>
          </template>

          <template v-else>
            <v-col cols="12">
              <v-radio-group
                v-model="localValue"
                @update:model-value="$emit('change', $event)"
                ref="radioButton"
                :label="multipleBooleanFields ? label : undefined"
                :required="mandatory || undefined"
                :rules="rules"
                class="mt-0"
                data-cy="field-input"
                hide-details
              >
                <v-radio
                  :disabled="locked || undefined"
                  :label="$bct(trueLabel)"
                  :readonly="readonly || undefined"
                  :value="true"
                  class="bg-super-light-blue pa-2 bc-extra-light-gray b-1 my-1"
                />

                <v-radio
                  v-if="falseLabel"
                  :disabled="locked || undefined"
                  :label="$bct(falseLabel)"
                  :readonly="readonly || undefined"
                  :value="false"
                  class="bg-super-light-blue pa-2 bc-extra-light-gray b-1 my-1"
                />
              </v-radio-group>
            </v-col>

            <v-col class="d-flex justify-end">
              <v-btn
                @click="clearResponse"
                :disabled="locked || undefined"
                class="b-1 my-1 fs-14"
                data-cy="clear_response_btn"
                size="small"
                variant="outlined"
              >
                {{ $bct('Clear response') }}
              </v-btn>
            </v-col>
          </template>
        </v-row>
      </div>

      <div v-if="properties.type == 'integer'">
        <v-row dense>
          <v-col>
            <template
              v-if="properties.maximum && properties.maximum - (properties.minimum || 0) < 100"
            >
              <v-select
                v-model.number="localValue"
                @update:model-value="$emit('change', $event)"
                :append-inner-icon="appendInnerIcon"
                :aria-label="`${$bct('Select')} ${label}`"
                :density="density"
                :disabled="locked || undefined"
                :items="maxMinArray"
                :readonly="readonly || undefined"
                :required="mandatory || undefined"
                :rules="rules"
                data-cy="field-input"
                variant="filled"
                clearable
                hide-details
              />
            </template>

            <template v-else>
              <v-textarea
                v-if="objectArraysAsDataTable"
                v-model.number="localValue"
                @change="$emit('change', $event)"
                @update:model-value="inputEventHandler(properties)"
                :append-inner-icon="appendInnerIcon"
                :aria-label="`${$bct('Enter')} ${label}`"
                :autofocus="autofocus"
                :disabled="locked || undefined"
                :readonly="readonly || undefined"
                :required="mandatory || undefined"
                :rules="rules"
                data-cy="field-input"
                density="compact"
                rows="1"
                variant="filled"
                auto-grow
                hide-details
                no-resize
                tile
              />

              <v-text-field
                v-else
                v-model.number="localValue"
                @change="$emit('change', $event)"
                @update:model-value="inputEventHandler(properties)"
                :append-inner-icon="appendInnerIcon"
                :aria-label="`${$bct('Enter')} ${label}`"
                :autofocus="autofocus"
                :density="density"
                :disabled="locked || undefined"
                :readonly="readonly || undefined"
                :required="mandatory || undefined"
                :rules="rules"
                data-cy="field-input"
                variant="filled"
                hide-details
                tile
              />
            </template>
          </v-col>
        </v-row>
      </div>

      <div v-if="properties.type == 'number'">
        <v-row dense>
          <v-col>
            <template v-if="properties.role == 'currency-us'">
              <VCurrencyField
                v-model.number="localValue"
                @change="handleMaybeNumberChange($event)"
                @update:model-value="inputEventHandler(properties)"
                :append-inner-icon="appendInnerIcon"
                :aria-label="`${$bct('Enter')} ${label}`"
                :autofocus="autofocus"
                :density="density"
                :disabled="locked || undefined"
                :readonly="readonly || undefined"
                :required="mandatory || undefined"
                :rules="rules"
                data-cy="field-input"
                variant="filled"
                hide-details
                tile
              />
            </template>

            <template v-else>
              <template
                v-if="properties.maximum && properties.maximum - (properties.minimum || 0) < 100"
              >
                <v-select
                  v-model.number="localValue"
                  @update:model-value="$emit('change', $event)"
                  :append-inner-icon="appendInnerIcon"
                  :aria-label="`${$bct('Select')} ${label}`"
                  :density="density"
                  :disabled="locked || undefined"
                  :items="maxMinArray"
                  :readonly="readonly || undefined"
                  :required="mandatory || undefined"
                  :rules="rules"
                  data-cy="field-input"
                  variant="filled"
                  clearable
                  condensed
                  hide-details
                />
              </template>

              <template v-else>
                <v-textarea
                  v-if="objectArraysAsDataTable"
                  v-model.number="localValue"
                  @change="$emit('change', $event)"
                  @update:model-value="inputEventHandler(properties)"
                  :append-inner-icon="appendInnerIcon"
                  :aria-label="`${$bct('Enter')} ${label}`"
                  :autofocus="autofocus"
                  :disabled="locked || undefined"
                  :readonly="readonly || undefined"
                  :required="mandatory || undefined"
                  :rules="rules"
                  data-cy="field-input"
                  density="compact"
                  rows="1"
                  type="number"
                  variant="filled"
                  auto-grow
                  hide-details
                  no-resize
                  tile
                />

                <v-text-field
                  v-else
                  v-model.number="localValue"
                  @change="$emit('change', $event)"
                  @update:model-value="inputEventHandler(properties)"
                  :append-inner-icon="appendInnerIcon"
                  :aria-label="`${$bct('Enter')} ${label}`"
                  :autofocus="autofocus"
                  :density="density"
                  :disabled="locked || undefined"
                  :readonly="readonly || undefined"
                  :required="mandatory || undefined"
                  :rules="rules"
                  data-cy="field-input"
                  type="number"
                  variant="filled"
                  hide-details
                  tile
                />
              </template>
            </template>
          </v-col>
        </v-row>
      </div>

      <div v-if="properties.type == 'string'">
        <template v-if="properties.enum && properties.enum.length > 0">
          <template v-if="condensed || properties.enum.length > 5">
            <v-select
              v-model="localValue"
              @update:model-value="
                $emit('change', $event);
                inputEventHandler(properties);
              "
              :append-inner-icon="appendInnerIcon"
              :aria-label="`${$bct('Select')} ${label}`"
              :density="density"
              :disabled="locked || undefined"
              :items="enumItems"
              :readonly="readonly || undefined"
              :required="mandatory || undefined"
              :rules="rules"
              data-cy="field-input"
              variant="filled"
              clearable
              hide-details
              tile
            />
          </template>

          <template v-else>
            <v-row dense>
              <v-col cols="12">
                <v-radio-group
                  v-model="localValue"
                  @update:model-value="$emit('change', $event)"
                  ref="radioButton"
                  :required="mandatory || undefined"
                  :rules="rules"
                  class="mt-0"
                  data-cy="field-input"
                  hide-details
                >
                  <v-radio
                    v-for="(option, index) in properties.enum"
                    :key="index"
                    :disabled="locked || undefined"
                    :label="$bct(option)"
                    :readonly="readonly || undefined"
                    :value="option"
                    class="bg-super-light-blue pa-2 bc-extra-light-gray b-1 my-1"
                  />
                </v-radio-group>
              </v-col>

              <v-col class="d-flex justify-end">
                <v-btn
                  @click="clearResponse"
                  :disabled="locked || undefined"
                  class="b-1 my-1 fs-14"
                  size="small"
                  variant="outlined"
                >
                  {{ $bct('Clear response') }}
                </v-btn>
              </v-col>
            </v-row>
          </template>
        </template>

        <template v-if="properties.format == 'date'">
          <v-row dense>
            <LabeledDate
              v-model="localValue"
              @change="$emit('change', $event)"
              :autofocus="autofocus"
              :dense="dense || veryDense"
              :disabled="locked || undefined"
              :hard-lock="locked || undefined"
              :maximum="maxDate"
              :minimum="minDate"
              :readonly="readonly || undefined"
              :required="mandatory || undefined"
              :rules="rules"
              data-cy="field-input"
            />
          </v-row>
        </template>

        <template v-if="properties.role == 'phone-us'">
          <MaskedInput
            v-model="localValue"
            v-slot="{ inputRef, masked }"
            mask="(###) ###-####"
          >
            <v-textarea
              v-if="objectArraysAsDataTable"
              v-model="masked.value"
              @change="$emit('change', $event)"
              :ref="inputRef"
              :append-inner-icon="appendInnerIcon"
              :aria-label="$bct('Enter phone number')"
              :autofocus="autofocus"
              :disabled="locked || undefined"
              :readonly="readonly || undefined"
              :required="mandatory || undefined"
              :rules="rules"
              data-cy="field-input"
              density="compact"
              placeholder="(555) 555-5555"
              rows="1"
              variant="filled"
              auto-grow
              hide-details
              no-resize
              tile
            />

            <v-text-field
              v-else
              v-model="masked.value"
              @change="$emit('change', $event)"
              :ref="inputRef"
              :append-inner-icon="appendInnerIcon"
              :aria-label="$bct('Enter phone number')"
              :autofocus="autofocus"
              :density="density"
              :disabled="locked || undefined"
              :readonly="readonly || undefined"
              :required="mandatory || undefined"
              :rules="rules"
              data-cy="field-input"
              placeholder="(555) 555-5555"
              variant="filled"
              hide-details
              tile
            />
          </MaskedInput>
        </template>

        <template v-if="properties.role == 'text-long'">
          <v-textarea
            v-if="!locked"
            v-model="localValue"
            @change="$emit('change', $event)"
            @update:model-value="inputEventHandler(properties)"
            :append-inner-icon="appendInnerIcon"
            :aria-label="label"
            :autofocus="autofocus"
            :density="density"
            :disabled="locked || undefined"
            :readonly="readonly || undefined"
            :required="mandatory || undefined"
            :rules="rules"
            data-cy="field-input"
            variant="filled"
          />

          <div
            v-else
            class="disabled-textarea-container"
          >
            <v-icon icon="lock" />

            <div
              v-html="safeLocalValue"
              class="textarea-content"
            />
          </div>
        </template>

        <template v-if="showMasked">
          <MaskedInput
            v-model="localValue"
            @update:model-value="inputEventHandler(properties)"
            v-slot="{ inputRef, masked }"
            :mask="properties.mask"
          >
            <v-textarea
              v-if="objectArraysAsDataTable"
              v-model="masked.value"
              @change="$emit('change', $event)"
              :ref="inputRef"
              :append-inner-icon="appendInnerIcon"
              :aria-label="`${$bct('Enter')} ${label}`"
              :autofocus="autofocus"
              :disabled="locked || undefined"
              :placeholder="properties.placeholder"
              :readonly="readonly || undefined"
              :required="mandatory || undefined"
              :rules="rules"
              data-cy="field-input"
              density="compact"
              rows="1"
              variant="filled"
              auto-grow
              hide-details
              no-resize
              tile
            />

            <v-text-field
              v-else
              v-model="masked.value"
              @change="$emit('change', $event)"
              :ref="inputRef"
              :append-inner-icon="appendInnerIcon"
              :aria-label="`${$bct('Enter')} ${label}`"
              :autofocus="autofocus"
              :density="density"
              :disabled="locked || undefined"
              :placeholder="properties.placeholder"
              :readonly="readonly || undefined"
              :required="mandatory || undefined"
              :rules="rules"
              data-cy="field-input"
              variant="filled"
              hide-details
              tile
            />
          </MaskedInput>
        </template>
      </div>

      <p
        v-if="displayAnswer"
        class="my-1 fw-600"
      >
        {{ $bct('Answer') }}: {{ answer }}
      </p>
    </template>
  </div>
</template>

<script setup>
import _ from 'lodash';
import { useDisplay } from 'vuetify/lib/framework.mjs';
import { useStore } from 'vuex';

import { capitalize } from '@/plugins/filters';
import DataTable from '@/shared/components/table/DataTable.vue';
import { dateMaximum, dateMinimum } from '@/shared/services/schema-date-validation';
import LabeledControl from '@/shared/components/form/LabeledControl.vue';
import LabeledDate from '@/shared/components/form/LabeledDate.vue';
import MaskedInput from '@/shared/components/form/MaskedInput.vue';
import VCurrencyField from '@/shared/components/form/VCurrencyField.vue';

const display = useDisplay();
const store = useStore();

const $bct = inject('translator');

const props = defineProps({
  answer: {
    type: String,
    default: null,
  },
  answerType: {
    type: String,
    default: null,
  },
  autofocus: {
    type: Boolean,
    default: undefined,
  },
  booleansAsCheckboxes: {
    type: Boolean,
    default: undefined,
  },
  condensed: {
    type: Boolean,
    default: undefined,
  },
  dense: {
    type: Boolean,
    default: undefined,
  },
  displayAnswer: {
    type: Boolean,
    default: undefined,
  },
  falseLabel: {
    type: String,
    default: null,
  },
  readonly: {
    type: Boolean,
    default: undefined,
  },
  landscape: {
    type: Boolean,
    default: undefined,
  },
  mandatory: {
    type: Boolean,
    default: undefined,
  },
  multipleBooleanFields: {
    type: Boolean,
    default: undefined,
  },
  objectArraysAsDataTable: {
    type: Boolean,
    default: undefined,
  },
  processing: {
    type: Boolean,
    default: undefined,
  },
  properties: {
    type: Object,
    required: true,
    default() {
      return {};
    },
    validator(value) {
      // Only validate in development/test environments to avoid production issues
      if (!['development', 'test'].includes(import.meta.env.NODE_ENV)) return true;

      // Prevent impossible showMasked states
      if (value.enum?.length > 0 && value.role && value.format === 'date') {
        console.error(
          'Invalid Field props: cannot have enum, role, and date format simultaneously',
        );
        return false;
      }

      // Ensure at least one rendering path is available
      const hasEnum = value.enum?.length > 0;
      const hasDateFormat = value.format === 'date';
      const hasRole = !!value.role;
      const hasMask = !!value.mask;

      if (!hasEnum && !hasDateFormat && !hasRole && !hasMask) {
        console.error('Invalid Field props: no valid rendering path available');
        return false;
      }

      return true;
    },
  },
  siblingValues: {
    type: Object,
    default() {
      return {};
    },
  },
  trueLabel: {
    type: String,
    default: null,
  },
  veryDense: {
    type: Boolean,
    default: undefined,
  },
});

const modelValue = defineModel({ type: undefined });

const emit = defineEmits(['change', 'change:input', 'update:modelValue']);

const emittedMountedChangeEvent = ref(false);
const localValue = ref(getInitialValue());
const objectArraysModelValue = ref(0);
const radioButton = ref(null);

watch(
  () => modelValue.value,
  (newValue, oldValue) => {
    if (newValue === oldValue || (newValue?.length === 0 && !oldValue)) return;

    localValue.value = newValue;
  },
);

watch(
  () => localValue.value,
  (newVal) => {
    emit('update:modelValue', newVal);
  },
  { deep: true },
);

const appendInnerIcon = computed(() => {
  return locked.value ? 'lock' : undefined;
});

const safeLocalValue = computed(() => {
  return localValue.value?.replace(/\n/g, '<br>');
});

const checkboxLabel = computed(() => {
  return capitalize(label.value);
});

const maxDate = computed(() => {
  return dateMaximum(props.properties, props.siblingValues);
});

const minDate = computed(() => {
  return dateMinimum(props.properties, props.siblingValues);
});

const showMasked = computed(() => {
  return (
    !(props.properties.enum?.length > 0) &&
    props.properties.format != 'date' &&
    !props.properties.role
  );
});

const density = computed(() => {
  return props.dense || props.veryDense || props.objectArraysAsDataTable ? 'compact' : undefined;
});

const enumItems = computed(() => {
  if (props.properties.type === 'array') {
    return props.properties.items.enum?.map((item) => ({
      text: $bct(item),
      value: item,
    }));
  }

  if (props.properties.type === 'string' && props.properties.enum) {
    return props.properties.enum?.map((item) => ({
      text: $bct(item),
      value: item,
    }));
  }

  return [];
});

const itemFields = computed(() => {
  return _.sortBy(
    Object.entries(props.properties.items?.properties || {}),
    (object) => object[1].index,
  );
});

const groupedFieldCols = computed(() => {
  if (display.smAndDown.value) return '12';

  return Math.max(3, 12 / itemFields.value.length).toString();
});

const label = computed(() => {
  return $bct(props.properties.alias || props.properties.title);
});

const locked = computed(() => {
  if (props.readonly) return true;
  if (props.properties?.readOnly) return true;

  // TODO: Determine how to handle null/undefined editRoles.
  // We may need to handle top-level schema properties differently from properties in object arrays
  // Historically we didn't used to allow editRoles to be set within properties in an array of objects field
  // We also could potentially have visible questions without editRoles where the user can't save a change to the field
  // but the UI would make it look like they could.
  // Data state isn't ready to support this guard clause and we need to decide on handling of nested attributes
  // if (!props.properties?.editRoles) return true;

  if (Array.isArray(props.properties?.editRoles)) {
    return !props.properties.editRoles.includes(store.state.role);
  }

  return false;
});

const maxMinArray = computed(() => {
  if (props.properties.maximum && props.properties.minimum !== undefined) {
    return [...Array(props.properties.maximum - (props.properties.minimum || 0)).keys()].map(
      (item) => item + (props.properties.minimum || 0),
    );
  }
  return [];
});

const rules = computed(() => {
  const rulesData = [];

  if (props.mandatory) {
    const rule = async (value) =>
      (value !== null && value !== undefined && value.length !== 0) ||
      `${label.value} ${$bct('is required')}`;
    rulesData.push(rule);
  }

  return rulesData;
});

const tableHeaders = computed(() => {
  const colsToPixels = (cols) => {
    // Reasonable approximation
    const tableWidth = display.smAndDown.value ? 250 : 1000;
    const pixels = (tableWidth / 12) * parseInt(cols, 10);
    return pixels + 'px';
  };

  const fieldHeaders = itemFields.value.map((field) => {
    let cols = groupedFieldCols.value;

    if (field[1].format == 'date') {
      const properties = field[1];
      const fieldMaxDate = dateMaximum(properties, {});
      const fieldMinDate = dateMinimum(properties, {});

      // Date can be either three labeled inputs or a single date picker
      cols = fieldMinDate === undefined && fieldMaxDate === undefined ? '7' : '3';
    }

    return {
      title: $bct(field[1].alias || field[1].title),
      value: field[0],
      minWidth: colsToPixels(cols),
    };
  });

  if (!locked.value) fieldHeaders.push({ value: 'remove', align: 'center' });

  return fieldHeaders;
});

function addItem(props) {
  return localValue.value.push(
    Object.keys(props).reduce((accumulator, value) => ({ ...accumulator, [value]: '' }), {}),
  );
}

function clearResponse() {
  localValue.value = null;
  emit('change', null);
}

function getInitialValue() {
  if (!modelValue.value && props.properties.type === 'array') return [];

  return modelValue.value;
}

function handleMaybeNumberChange(event) {
  // Event can sometimes be an object, so we need to check if it's a number
  if (typeof event !== 'number' && event != null) return;

  // Hack in place until this is resolved: https://github.com/dm4t2/vue-currency-input/issues/421
  if (!emittedMountedChangeEvent.value) {
    emittedMountedChangeEvent.value = true;
    return;
  }

  emit('change', event);
}

const inputEventHandler = _.debounce((properties) => {
  if (properties?.mask) return;

  emit('change:input');
}, 100);

function load() {
  if (props.properties.type === 'boolean' && props.autofocus) radioButton.value.focus();
  if (props.objectArraysAsDataTable) objectArraysModelValue.value = 1;
}

function removeItemByIndex(index) {
  localValue.value.splice(index, 1);
}

onMounted(load);
</script>

<style scoped lang="scss">
.pb-4-table > * {
  padding-bottom: 8px !important;
}

.disabled-textarea-container {
  background-color: rgba(#eee, 40%);
  color: darken(#bdbdbd, 13%);
  font-size: 1rem;
  height: 136px;
  position: relative;
  width: 100%;

  i.v-icon {
    position: absolute;
    right: 10px;
    top: 10px;
  }

  .textarea-content {
    height: 136px;
    overflow-y: auto;
    padding: 10px 36px 10px 16px;
    white-space: pre-wrap;
    width: 100%;
  }
}
.v-selection-control {
  > :deep(.v-label) {
    width: 100%;
    padding-left: 56px;
    z-index: -1;
  }
}
</style>
